import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';
import linkstate from 'linkstate';
import md5 from 'crypto-js/md5';
import { unlinkedMedicineLineFields, withUnlinkedMedicineQtyAdded } from '../../lib/journalMedicineProducts';
import { sortRetentionPeriodsForDisplay } from '../../lib/retentionPeriodDisplay';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';

import ViewFields from '../../components/form/viewFields';
import Input from '../../components/form/input';
import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import FelleskatalogenLink from './felleskatalogenLink';
import DrugProductLinkBadge, { loadDrugProductMap } from './drugProductLinkBadge';
import { appAlert } from '../../lib/dialogs';

function isValidDate(dateString) {
    // console.log('isValidDate', dateString);

    const date = new Date(dateString);
    // Check if the date is invalid
    if (isNaN(date)) {
        // console.log('isNaN(date)', isNaN(date));
        return false;
    }
    // Check if the year is before 1800
    if (date.getFullYear() < 1900) {
        // console.log('date.getFullYear() < 1900', date.getFullYear() < 1900);
        return false;
    }
    // If we reach here, the date is valid
    return true;
}

/**
 * Merge drug's original tilbakeholdsfrister with custom retentionPeriods from template.
 * Custom entries take priority for matching type+species combinations.
 */
function mergeRetentionPeriods(tilbakeholdsfrister = [], retentionPeriods = []) {
    if (!tilbakeholdsfrister.length && !retentionPeriods.length) return [];
    if (!tilbakeholdsfrister.length) return retentionPeriods;
    if (!retentionPeriods.length) return tilbakeholdsfrister;

    const merged = JSON.parse(JSON.stringify(tilbakeholdsfrister));

    retentionPeriods.forEach(customEntry => {
        const existingTypeIdx = merged.findIndex(m => m.type === customEntry.type);
        if (existingTypeIdx >= 0) {
            const existingDyreslag = merged[existingTypeIdx].dyreslag || [];
            (customEntry.dyreslag || []).forEach(customDyreslag => {
                const existingSpeciesIdx = existingDyreslag.findIndex(
                    d => d.dyreslagkode === customDyreslag.dyreslagkode,
                );
                if (existingSpeciesIdx >= 0) {
                    existingDyreslag[existingSpeciesIdx] = { ...customDyreslag };
                } else {
                    existingDyreslag.push({ ...customDyreslag });
                }
            });
            merged[existingTypeIdx].dyreslag = existingDyreslag;
        } else {
            merged.push(JSON.parse(JSON.stringify(customEntry)));
        }
    });

    return merged;
}

@withText(props => ({
    drugTitle: <Text id='dyrejournal.drug.search-title'>Search for drug</Text>,
    retentionDays: <Text id='input.retention-days'>Retantion days</Text>,
}))
@observer
class AddMedicine extends Component {
	constructor(props) {
        super(props);
        this.state = {
            selectedItem: {},
            inputRetention: {},
            standardSaved: false,
            drugProductMap: {},
        };
        this.searchTimer = null;
        this.focusSearch = null;
        this.amountPerAnimalRef = null;
    }

    loadAll = async (props = this.props, emptyFirst = true) => {
        const { productStore } = this.props.stores;
        // Which of the clinic's products are linked to a drug (TICKET-01695):
        // lets the search rows mark the pack size that carries the clinic's own price.
        const drugProductMap = await loadDrugProductMap(productStore);
        this.setState({ drugProductMap });
    }

    onClickChooseDrug = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('tr').dataset;
        const { drawerLevel = 1, id: journalId } = this.props;
		const { appState, saleStore, drugStore } = this.props.stores;
        const { newSale } = saleStore;
        const { drugs = [] } = newSale;
        const { drugs: allDrugs } = drugStore;
        const selectedItem = allDrugs.find(d => d.id === parseInt(id, 10));
        if (selectedItem) {
            const { journals = [] } = newSale;
            // Add drug to journal with id journalId
            const journal = journals.find(j => j.id === parseInt(journalId, 10));
            appState.toggleDrawer(false, drawerLevel);
        }
    }

    getJournalAnimal = () => {
        const { id: jId, object } = this.props;
        const { saleStore, journalStore } = this.props.stores;
        const { newSale, animalJournals: saleAnimalJournals } = saleStore;
        const { journals: fallbackJournals, animalJournals: journalStoreAnimalJournals } = journalStore;

        const journalId = parseInt(jId || object?.id, 10);

        // Search in ALL arrays for the journal
        let journal = null;

        if (fallbackJournals && fallbackJournals.length > 0) {
            journal = fallbackJournals.find(j => j.id === journalId);
        }
        if (!journal && journalStoreAnimalJournals && journalStoreAnimalJournals.length > 0) {
            journal = journalStoreAnimalJournals.find(j => j.id === journalId);
        }
        if (!journal && saleAnimalJournals && saleAnimalJournals.length > 0) {
            journal = saleAnimalJournals.find(j => j.id === journalId);
        }
        if (!journal && object && object.id === journalId) {
            journal = object;
        }
        if (!journal && object && !object.id) {
            journal = object;
        }

        if (!journal) {
            console.log('getJournalAnimal: journal not found');
            return null;
        }

        // Get the animal ID from the journal
        const animalId = typeof journal.animal === 'object' ? journal.animal.id : journal.animal;

        // Find the full animal object from newSale.animals (same as searchProductCallback in sale.js)
        const { animals = [] } = newSale;
        const animal = animals.find(a => a.id === animalId);

        return animal || null;
    }

    loadAndAddRelatedProduct = async (medicineData) => {
        const { productStore, saleStore, userStore } = this.props.stores;
        const { saveSale } = this.props;
        const { newSale } = saleStore;
        const { user, customer } = userStore;

        try {
            // Get the animal from the current journal
            const animal = this.getJournalAnimal();
            const { products = [] } = newSale;

            // Try to find a linked product for this drug
            const response = await productStore.load({
                query: { drug: medicineData.id },
                limit: 1,
                skipUpdate: true,
            });

            let productLine;

            if (response && response.length > 0) {
                // Use the linked product
                const product = response[0];

                // Check for duplicates (linked product + same animal)
                const existingProduct = products.find(p => p.id === product.id && p.animal?.id === animal?.id);
                if (existingProduct) {
                    return;
                }

                productLine = {
                    ...product,
                    qty: parseFloat(medicineData.amountPerAnimal) || 1,
                    createdDate: new Date(),
                    md5: md5(JSON.stringify({ ...product, createdDate: new Date(), animal })).toString(),
                    saleUser: user.id,
                    saleDepartment: user.currentCustomerDepartment,
                };
            } else {
                // No linked product — create product line from medicine data.
                // Same drug already on the sale for this animal: add to its qty
                // instead of silently dropping the second dose (TICKET-01713).
                const qty = parseFloat(medicineData.amountPerAnimal) || 1;
                const bumped = withUnlinkedMedicineQtyAdded(products, { drugId: medicineData.id, animalId: animal?.id, qty });
                if (bumped) {
                    saleStore.updateObjectKeyValue('newSale', 'products', bumped);
                    saleStore.updateObjectKeyValue('newSale', 'productAdded', true);
                    if (saveSale && typeof saveSale === 'function') {
                        await saveSale({ calledFrom: 'addMedicine.loadAndAddRelatedProduct', fieldsToSave: ['products'] });
                    } else {
                        await saleStore.saveSale({ calledFrom: 'addMedicine.loadAndAddRelatedProduct', fieldsToSave: ['products'] });
                    }
                    return;
                }

                productLine = {
                    name: medicineData.fullname || medicineData.name,
                    price: medicineData.price || 0,
                    qty,
                    drug: medicineData.id,
                    ...unlinkedMedicineLineFields(medicineData, customer),
                    createdDate: new Date(),
                    md5: md5(JSON.stringify({ drug: medicineData.id, createdDate: new Date(), animal })).toString(),
                    saleUser: user.id,
                    saleDepartment: user.currentCustomerDepartment,
                };
            }

            // Add animal to product line if available
            if (animal) {
                productLine.animal = {
                    id: animal.id,
                    name: animal.name,
                };
            }

            // Add to sale products
            saleStore.updateObjectKeyValue('newSale', 'products', [...products, productLine]);

            // Save to database - mark products as updated first!
            saleStore.updateObjectKeyValue('newSale', 'productAdded', true);

            if (saveSale && typeof saveSale === 'function') {
                await saveSale({ calledFrom: 'addMedicine.loadAndAddRelatedProduct', fieldsToSave: ['products'] });
            } else {
                // Fallback: save directly via store when saveSale prop is not available
                // (e.g., when addMedicine is opened from Input component's drawer chain)
                await saleStore.saveSale({ calledFrom: 'addMedicine.loadAndAddRelatedProduct', fieldsToSave: ['products'] });
            }
        } catch (error) {
            console.error('Failed to load related product:', error);
            // Fail silently - don't disrupt the drug addition flow
        }
    }

    searchDrugCallback = selectedItem => {
        const finalSelectedItem = JSON.parse(JSON.stringify(selectedItem));

        // Get journal date to use instead of today
        const { id: jId, object } = this.props;
        const { saleStore, journalStore } = this.props.stores;
        const { newSale, animalJournals: saleAnimalJournals } = saleStore;
        const { journals: fallbackJournals, animalJournals: journalStoreAnimalJournals } = journalStore;
        const journalId = parseInt(jId || object?.id, 10);

        let journal = null;
        if (fallbackJournals?.length > 0) {
            journal = fallbackJournals.find(j => j.id === journalId);
        }
        if (!journal && journalStoreAnimalJournals?.length > 0) {
            journal = journalStoreAnimalJournals.find(j => j.id === journalId);
        }
        if (!journal && saleAnimalJournals?.length > 0) {
            journal = saleAnimalJournals.find(j => j.id === journalId);
        }
        if (!journal && object && object.id === journalId) {
            journal = object;
        }
        if (!journal && object && !object.id) {
            journal = object;
        }

        // Use journal date or fall back to today
        const journalDate = journal?.date ? new Date(journal.date) : new Date();
        const lastThreatmentDate = util.isoDate(journalDate, { dateTimeSep: 'T' });

        const inputMedicine = {
            id: finalSelectedItem.id,
            brandId: finalSelectedItem.pakningsinfoRefLegemiddelMerkevare || finalSelectedItem['medisin-merke-id'],
            name: finalSelectedItem.varenavn || finalSelectedItem.navn,
            fullname: finalSelectedItem.navnFormStyrke || finalSelectedItem.navn,
            uuidv4: finalSelectedItem.uuidv4,
            amountPerAnimal: finalSelectedItem.amountPerAnimal || '',
            unit: finalSelectedItem.enhetDoseringDn || finalSelectedItem.unit,
            routeOfAdministration: finalSelectedItem.routeOfAdministration || '',
            no: finalSelectedItem.no ? finalSelectedItem.no : `${finalSelectedItem.pakningsinfoPakningsstr || ''} ${finalSelectedItem.pakningsinfoEnhetPakningV || ''}`,
            lastThreatment: lastThreatmentDate,
            usageReason: finalSelectedItem.usageReason || '',
            threatmentBy: finalSelectedItem.threatmentBy || '',
            retentionPeriods: mergeRetentionPeriods(
                finalSelectedItem.tilbakeholdsfrister,
                finalSelectedItem.retentionPeriods,
            ),
            productNumber: finalSelectedItem.varenr || finalSelectedItem.productNumber || '',
            price: finalSelectedItem.prisAUP || 0,
        };

        const { drugStore } = this.props.stores;
        drugStore.updateKeyValue('inputMedicine', inputMedicine);
        this.setState({ selectedItem }, () => {
            // Focus on amountPerAnimal field after medicine is selected
            // Delay to override the search component's refocus (which happens after 50ms)
            setTimeout(() => {
                this.amountPerAnimalRef?.focus();
                this.amountPerAnimalRef?.select();
            }, 100);
        });
    }

    addRetentionPeriode = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id: journalId, object } = this.props;
        const { selectedItem } = this.state;
        const { drugStore, journalStore } = this.props.stores;
        const { inputMedicine, inputRetention } = drugStore;
        const { journal } = journalStore;

        // FIX: Validate input. retentionDays may legitimately be 0 (no withholding) or -1 (livstid),
        // and 0 is falsy — so check that a value was provided rather than that it is truthy.
        const retentionDaysProvided = inputRetention.retentionDays !== undefined
            && inputRetention.retentionDays !== null
            && inputRetention.retentionDays !== '';
        if (!inputRetention.retentionType || !inputRetention.retentionDyreslag || !retentionDaysProvided) {
            (await appAlert('Please fill in all retention period fields'));
            return;
        }

        const { retentionPeriods = [] } = inputMedicine;
        // console.log('addRetentionPeriode', retentionPeriods);
        const newDyreslag = {
            dyreslagkode: inputRetention.retentionDyreslag,
            'antall-dager': inputRetention.retentionDays,
        };

        // Merge into an existing entry of the same type so the backend's .find(type === ...)
        // sees a single consolidated entry with all dyreslag. Pushing a separate entry causes
        // the backend to miss user-added species (bug: Animalia reported 0 days for added species).
        const newRetentionPeriods = JSON.parse(JSON.stringify(retentionPeriods));
        const existingTypeIdx = newRetentionPeriods.findIndex(r => r && r.type === inputRetention.retentionType);
        if (existingTypeIdx >= 0) {
            const existingDyreslag = newRetentionPeriods[existingTypeIdx].dyreslag || [];
            const existingSpeciesIdx = existingDyreslag.findIndex(d => d && d.dyreslagkode === newDyreslag.dyreslagkode);
            if (existingSpeciesIdx >= 0) {
                existingDyreslag[existingSpeciesIdx] = newDyreslag;
            } else {
                existingDyreslag.push(newDyreslag);
            }
            newRetentionPeriods[existingTypeIdx].dyreslag = existingDyreslag;
        } else {
            newRetentionPeriods.push({
                type: inputRetention.retentionType,
                dyreslag: [newDyreslag],
            });
        }
        drugStore.updateObjectKeyValue('inputMedicine', 'retentionPeriods', newRetentionPeriods);

        // Get the correct journal ID (from props, object, or journalStore)
        const actualJournalId = journalId || object?.id || journal?.id;

        if (selectedItem && selectedItem.md5 && actualJournalId) {
            // FIX: Add error handling
            try {
                await journalStore.editArrayObject({
                    field: 'medicine',
                    key: 'retentionPeriods',
                    value: newRetentionPeriods,
                    id: actualJournalId,
                    md5: selectedItem.md5,
                });
            } catch (error) {
                console.error('Failed to add retention period', error);
                (await appAlert('Failed to add retention period. Please try again.'));
            }
        }
    }

    toggleAddRetentionPeriode = e => {
        e.preventDefault();
        e.stopPropagation();
        const { showAddRetentionPeriode } = this.state;
        this.setState({ showAddRetentionPeriode: !showAddRetentionPeriode });
    }

    onClickAddDrugToJournal = async () => {
        const { saleStore, journalStore, drugStore } = this.props.stores;
        const { inputMedicine } = drugStore;
        const { id: jId, saleLoadJournals = () => {}, object, callback = () => {} } = this.props;

        // Validate input medicine
        if (!inputMedicine || !inputMedicine.id) {
            (await appAlert('Please search and select a medicine first'));
            return;
        }

        const { newSale, animalJournals: saleAnimalJournals } = saleStore;
        const { journals = [] } = newSale;
        const { journals: fallbackJournals, animalJournals: journalStoreAnimalJournals, newJournal } = journalStore;

        const journalId = parseInt(jId || object?.id, 10);

        // FIX: Search in ALL arrays instead of using priority logic
        // This prevents the bug where journal is in one array but we only search in another
        let journal = null;

        if (fallbackJournals && fallbackJournals.length > 0) {
            journal = fallbackJournals.find(j => j.id === journalId);
        }
        if (!journal && journalStoreAnimalJournals && journalStoreAnimalJournals.length > 0) {
            journal = journalStoreAnimalJournals.find(j => j.id === journalId);
        }
        if (!journal && saleAnimalJournals && saleAnimalJournals.length > 0) {
            journal = saleAnimalJournals.find(j => j.id === journalId);
        }

        // If journal not found in arrays, but we have a valid object prop, use it
        if (!journal && object && object.id === journalId) {
            journal = object;
            console.log('Using journal from object prop (not found in stores)', { journal });
        }
        // Also handle objects without ID (new journals)
        if (!journal && object && !object.id) {
            // This is a new journal without an ID - use the object directly
            journal = object;
            console.log('Using newJournal without ID (will save locally)', { journal });
        }

        console.log('onClickAddDrugToJournal', {
            journal,
            inputMedicine,
            journalId,
            hasJournalId: !!journal?.id,
            isNewJournal: journal && !journal.id,
            fallbackJournalsCount: fallbackJournals?.length,
            journalStoreAnimalJournalsCount: journalStoreAnimalJournals?.length,
            saleAnimalJournalsCount: saleAnimalJournals?.length
        });

        // FIX: Add proper error handling when journal is not found
        if (!journal) {
            console.error('Journal not found and no newJournal object available', {
                journalId,
                jId,
                object,
                fallbackJournals: fallbackJournals?.length,
                journalStoreAnimalJournals: journalStoreAnimalJournals?.length,
                saleAnimalJournals: saleAnimalJournals?.length
            });
            (await appAlert('Error: Could not find journal. Please refresh the page and try again.'));
            return;
        }

        // Use journal date or fall back to today
        const journalDate = journal?.date ? new Date(journal.date) : new Date();

        const value = {
            ...inputMedicine,
            md5: md5(JSON.stringify({ ...inputMedicine, date: journalDate })).toString(),
            date: journalDate,
        };

        try {
            const newMedicine = journal.medicine ? [...journal.medicine] : [];
            newMedicine.push(value);

            // OPTION 2: Handle differently based on whether journal has an ID
            if (journal.id) {
                // Journal exists in database - save to backend
                await journalStore.saveField(journal.id, 'medicine', value);

                // Update the source array (fallbackJournals, journalStoreAnimalJournals, or saleAnimalJournals)
                if (fallbackJournals && fallbackJournals.length > 0 && fallbackJournals.find(j => j.id === journal.id)) {
                    journalStore.updateField(journal.id, 'medicine', newMedicine);
                }
                if (journalStoreAnimalJournals && journalStoreAnimalJournals.length > 0) {
                    const animalJournalsCopy = [...journalStoreAnimalJournals];
                    const idx = animalJournalsCopy.findIndex(j => j.id === journal.id);
                    if (idx >= 0) {
                        animalJournalsCopy[idx] = { ...animalJournalsCopy[idx], medicine: newMedicine };
                        journalStore.updateKeyValue('animalJournals', animalJournalsCopy);
                    }
                }
                if (saleAnimalJournals && saleAnimalJournals.length > 0) {
                    const animalJournalsCopy = [...saleAnimalJournals];
                    const idx = animalJournalsCopy.findIndex(j => j.id === journal.id);
                    if (idx >= 0) {
                        animalJournalsCopy[idx] = { ...animalJournalsCopy[idx], medicine: newMedicine };
                        saleStore.updateKeyValue('animalJournals', animalJournalsCopy);
                    }
                }

                // Update newJournal if this is the journal being edited
                if (newJournal && newJournal.id === journal.id) {
                    journalStore.updateObjectKeyValue('newJournal', 'medicine', newMedicine);
                }

                await saleLoadJournals();
            } else {
                // OPTION 2: New journal without ID - update locally only
                // Medicine will be saved when journal is saved
                console.log('Adding medicine to newJournal locally (not saved to backend yet)');
                journalStore.updateObjectKeyValue('newJournal', 'medicine', newMedicine);
            }

            // Quietly load and add related product to sale (after medicine is successfully added)
            const { userStore } = this.props.stores;
            const { customer } = userStore;
            if (parseInt(customer?.featureFlags?.autoAddMedicineAsProduct, 10) === 1) {
                await this.loadAndAddRelatedProduct(inputMedicine);
            }

            // Execute callback and cleanup after successful operation
            // Await callback to ensure any focus changes it makes are complete
            await callback();
            drugStore.updateKeyValue('inputMedicine', {});
            drugStore.updateKeyValue('inputRetention', {});
            this.setState({ selectedItem: null }, () => {
                // Refocus search input after state update completes
                this.focusSearch?.();
            });
        } catch (error) {
            console.error('Failed to save medicine', error);
            (await appAlert('Failed to add medicine. Please try again.'));
        }
    }

    onClickDeleteDrugFromJournal = async e => {
        const { md5 } = e.target.closest('button').dataset;
        const { saleStore, journalStore } = this.props.stores;
        const { id: jId, saleLoadJournals = () => {}, object, callback = () => {} } = this.props;
        const { newSale, animalJournals: saleAnimalJournals } = saleStore;
        const { journals = [] } = newSale;
        const { journals: fallbackJournals, animalJournals: journalStoreAnimalJournals, newJournal } = journalStore;

        const journalId = parseInt(jId || object?.id, 10);

        // FIX: Search in ALL arrays instead of using priority logic
        let journal = null;

        if (fallbackJournals && fallbackJournals.length > 0) {
            journal = fallbackJournals.find(j => j.id === journalId);
        }
        if (!journal && journalStoreAnimalJournals && journalStoreAnimalJournals.length > 0) {
            journal = journalStoreAnimalJournals.find(j => j.id === journalId);
        }
        if (!journal && saleAnimalJournals && saleAnimalJournals.length > 0) {
            journal = saleAnimalJournals.find(j => j.id === journalId);
        }

        // If journal not found in arrays, but we have a valid object prop, use it
        if (!journal && object && object.id === journalId) {
            journal = object;
        }
        // Also handle objects without ID (new journals)
        if (!journal && object && !object.id) {
            journal = object;
        }

        // FIX: Add error handling
        if (!journal) {
            console.error('Journal not found in onClickDeleteDrugFromJournal', {
                journalId,
                jId,
                object,
                fallbackJournals: fallbackJournals?.length,
                journalStoreAnimalJournals: journalStoreAnimalJournals?.length,
                saleAnimalJournals: saleAnimalJournals?.length
            });
            (await appAlert('Error: Could not find journal. Please refresh the page.'));
            return;
        }

        try {
            // OPTION 2: Handle differently based on whether journal has an ID
            if (journal.id) {
                // Journal exists in database - delete from backend
                await journalStore.removeArrayObject({ field: 'medicine', id: journalId, md5 });
                callback();
                await saleLoadJournals();
            } else {
                // OPTION 2: New journal without ID - update locally only
                const newMedicine = (journal.medicine || []).filter(m => m.md5 !== md5);
                journalStore.updateObjectKeyValue('newJournal', 'medicine', newMedicine);
                callback();
            }
        } catch (error) {
            console.error('Failed to delete medicine', error);
            (await appAlert('Failed to delete medicine. Please try again.'));
        }
    }

    onClickEditLine = e => {
        const { md5 } = e.target.closest('tr').dataset;
        const { drugStore, journalStore, saleStore } = this.props.stores;
        const { id: jId, object } = this.props;
        const { animalJournals: saleAnimalJournals } = saleStore;
        const { journals: fallbackJournals, animalJournals: journalStoreAnimalJournals } = journalStore;

        const journalId = parseInt(jId || object?.id, 10);

        // Search in ALL arrays instead of using priority logic
        let journal = null;

        if (fallbackJournals && fallbackJournals.length > 0) {
            journal = fallbackJournals.find(j => j.id === journalId);
        }
        if (!journal && journalStoreAnimalJournals && journalStoreAnimalJournals.length > 0) {
            journal = journalStoreAnimalJournals.find(j => j.id === journalId);
        }
        if (!journal && saleAnimalJournals && saleAnimalJournals.length > 0) {
            journal = saleAnimalJournals.find(j => j.id === journalId);
        }

        // If journal not found in arrays, but we have a valid object prop, use it
        if (!journal && object && object.id === journalId) {
            journal = object;
        }
        // Also handle objects without ID (new journals)
        if (!journal && object && !object.id) {
            journal = object;
        }

        if (!journal) {
            console.error('Journal not found in onClickEditLine', { journalId, jId, object });
            void appAlert('Error: Could not find journal. Please refresh the page.');
            return;
        }

        const { medicine = [] } = journal;
        const foundMedicine = medicine.find(m => m.md5 === md5);

        if (!foundMedicine) {
            console.error('Medicine not found', { md5, medicine });
            void appAlert('Error: Could not find medicine. Please refresh the page.');
            return;
        }

        const selectedItem = JSON.parse(JSON.stringify(foundMedicine));
        this.setState({ selectedItem });
        drugStore.updateKeyValue('inputMedicine', { ...selectedItem});
        journalStore.updateKeyValue('journal', journal);
    }

    onInput = e => {
        const { journalStore, saleStore, drugStore } = this.props.stores;
        const { name, value, type } = e.target;
        const { md5 } = e.target.closest('input, select').dataset;

        const { journal } = journalStore;
        const idx = journal.medicine?.findIndex(m => m.md5 === md5);

        drugStore.updateObjectKeyValue('inputMedicine', name, value);

        // if (type === 'datetime-local' && !isValidDate(value)) {
        //     return false;
        // }
        // if (type === 'date' && !isValidDate(value)) {
        //     return false;
        // }

        // updateObjectKeyArrFieldValue(obj, key, idx, field, value) {
        journalStore.updateObjectKeyArrFieldValue('journal', 'medicine', idx, name, value);

        clearTimeout(this.updateTimer);
        this.updateTimer = setTimeout(() => {
            this.doUpdate(e);
        }, 1000);
    }

    onInputRetention = e => {
        const { drugStore } = this.props.stores;
        const { name } = e.target;
        let { value } = e.target;
        // DHP besøk API caps tilbakeholdsfrist at 999 days (livstid = -1 is set via the checkbox below).
        if (name === 'retentionDays') {
            const days = parseInt(value, 10);
            if (!Number.isNaN(days)) {
                value = Math.min(999, Math.max(0, days));
            }
        }
        drugStore.updateObjectKeyValue('inputRetention', name, value);
    }

    // Livstid (lifetime) withholding is encoded as antall-dager = -1 in the Animalia DHP besøk API.
    onToggleRetentionLivstid = e => {
        const { drugStore } = this.props.stores;
        const { checked } = e.target;
        drugStore.updateObjectKeyValue('inputRetention', 'retentionLivstid', checked);
        drugStore.updateObjectKeyValue('inputRetention', 'retentionDays', checked ? -1 : '');
    }

    doUpdate = async (e) => {
        const { id: journalId, object } = this.props;
        const { journalStore } = this.props.stores;
        const { newJournal } = journalStore;
        const { name, value } = e.target;
        const dataset = e.target.closest('input, select')?.dataset;
        if (!dataset) {
            // Element may have been detached from DOM (e.g. edit form closed)
            return;
        }
        const { md5 } = dataset;

        try {
            // OPTION 2: Handle differently based on whether journal has an ID
            if (journalId || (object && object.id)) {
                // Journal exists in database - update backend
                const response = await journalStore.editArrayObject({
                    field: 'medicine',
                    key: name,
                    value,
                    id: journalId || object.id,
                    md5,
                });
                if (response && response.status >= 400) {
                    console.error('Edit medicine failed with status', response.status);
                    (await appAlert('Failed to save changes. Please try again.'));
                }
            } else {
                // OPTION 2: New journal without ID - update locally only
                const medicine = newJournal.medicine || [];
                const idx = medicine.findIndex(m => m.md5 === md5);
                if (idx >= 0) {
                    const updatedMedicine = [...medicine];
                    updatedMedicine[idx] = { ...updatedMedicine[idx], [name]: value };
                    journalStore.updateObjectKeyValue('newJournal', 'medicine', updatedMedicine);
                }
            }
        } catch (error) {
            console.error('Failed to update medicine field', error);
            (await appAlert('Failed to save changes. Please try again.'));
        }
    }

    onClickEditDone = async () => {
        // Cancel any pending debounce timer to prevent stale updates after closing
        clearTimeout(this.updateTimer);
        clearTimeout(this.standardSavedTimer);

        this.setState({ selectedItem: null });
        const { journalStore, drugStore } = this.props.stores;
        const { saleLoadJournals = () => {} } = this.props;
        drugStore.updateKeyValue('inputMedicine', {});
        drugStore.updateKeyValue('inputRetention', {});
        journalStore.updateKeyValue('journal', {});

        // Reload journals from database to ensure all local arrays are synced
        await saleLoadJournals();
    }

    onClickRemoveSingleRetentionPeriod = async (e, retentionIdx) => {
        e.preventDefault();
        e.stopPropagation();

        const { id: journalId, object } = this.props;
        const { selectedItem } = this.state;
        const { drugStore, journalStore } = this.props.stores;
        const { inputMedicine } = drugStore;
        const { journal } = journalStore;
        const { retentionPeriods = [] } = inputMedicine;

        // Remove the retention period at the specified index
        const newRetentionPeriods = retentionPeriods.filter((_, idx) => idx !== retentionIdx);
        drugStore.updateObjectKeyValue('inputMedicine', 'retentionPeriods', newRetentionPeriods);

        // Get the correct journal ID (from props, object, or journalStore)
        const actualJournalId = journalId || object?.id || journal?.id;

        // If editing an existing medicine, save to backend
        if (selectedItem && selectedItem.md5 && actualJournalId) {
            try {
                await journalStore.editArrayObject({
                    field: 'medicine',
                    key: 'retentionPeriods',
                    value: newRetentionPeriods,
                    id: actualJournalId,
                    md5: selectedItem.md5,
                });
            } catch (error) {
                console.error('Failed to remove retention period', error);
                (await appAlert('Failed to remove retention period. Please try again.'));
            }
        }
    }

    displayRetentionPeriods = (retentionPeriods, editable = false) => {
        if (!retentionPeriods) {
            return '';
        }
        // Fixed display order (Slakt, then Produkt) — TICKET-01759. retentionIdx stays the
        // position in the stored array so the remove button targets the right entry.
        return sortRetentionPeriodsForDisplay(retentionPeriods).map(({ period: retention, index: retentionIdx }) => {
            return(<>
                <div class='d-flex flex-row align-items-center'>
                    {editable && (
                        <button
                            class='btn btn-link btn-sm text-danger p-0 me-1'
                            onClick={(e) => this.onClickRemoveSingleRetentionPeriod(e, retentionIdx)}
                            title='Remove this retention period'
                        >
                            <i class='fa-solid fa-times' />
                        </button>
                    )}
                    <div class='me-1 font-weight-bold'>{retention.type}</div>
                    <div class='d-flex flex-row'>
                        {retention.dyreslag.map((dyreslag, idx) => {
                            return(<>
                                <div class='me-1 text-muted'>{dyreslag.dyreslagkode}</div>
                                <div class='me-1'>{Number(dyreslag['antall-dager']) === -1
                                    ? <Text id='dyrejournal.retention-lifetime'>Lifetime</Text>
                                    : dyreslag['antall-dager']}</div>
                            </>);
                        })}
                    </div>
                </div>
            </>);
        });
    }

    onClickRemoveRetentionPeriode = async e => {
        e.preventDefault();
        e.stopPropagation();
        // key: 'retentionPeriods',
        const { id: journalId, object } = this.props;
        const { selectedItem } = this.state;
        const { drugStore, journalStore } = this.props.stores;
        const { inputMedicine, inputRetention } = drugStore;
        const { journal } = journalStore;

        drugStore.updateObjectKeyValue('inputMedicine', 'retentionPeriods', []);
        drugStore.updateKeyValue('inputRetention', {});

        // Get the correct journal ID (from props, object, or journalStore)
        const actualJournalId = journalId || object?.id || journal?.id;

        if (selectedItem && selectedItem.md5 && actualJournalId) {
            // FIX: Add error handling
            try {
                await journalStore.editArrayObject({
                    field: 'medicine',
                    key: 'retentionPeriods',
                    value: [],
                    id: actualJournalId,
                    md5: selectedItem.md5,
                });
            } catch (error) {
                console.error('Failed to remove retention periods', error);
                (await appAlert('Failed to remove retention periods. Please try again.'));
            }
        }
    }

    // Explicit "Lagre som standard" (TICKET-00441): saves the line's current withholding as the
    // clinic's per-medicine standard. This is the ONLY way withholding is remembered for later —
    // the journal save path never templates it (TICKET-01139).
    getStandardBrandId = () => {
        const { selectedItem } = this.state;
        const { drugStore } = this.props.stores;
        const { inputMedicine } = drugStore;
        return selectedItem?.['medisin-merke-id'] || inputMedicine?.brandId;
    }

    onClickSaveStandardRetention = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { drugStore } = this.props.stores;
        const { inputMedicine } = drugStore;
        const { retentionPeriods = [] } = inputMedicine;
        const brandId = this.getStandardBrandId();
        if (!brandId || retentionPeriods.length === 0) {
            return;
        }
        if (this.standardRequestInFlight) {
            return;
        }
        this.standardRequestInFlight = true;
        try {
            const response = await drugStore.saveStandardRetentionPeriods({ brandId, retentionPeriods });
            if (response && response.status < 400) {
                const { selectedItem } = this.state;
                const savedStandard = response.data?.standardRetentionPeriods || [...retentionPeriods];
                this.setState({
                    standardSaved: true,
                    selectedItem: { ...selectedItem, retentionPeriods: savedStandard },
                });
                clearTimeout(this.standardSavedTimer);
                this.standardSavedTimer = setTimeout(() => this.setState({ standardSaved: false }), 3000);
            } else {
                (await appAlert('Failed to save standard. Please try again.'));
            }
        } finally {
            this.standardRequestInFlight = false;
        }
    }

    onClickRemoveStandardRetention = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { drugStore } = this.props.stores;
        const brandId = this.getStandardBrandId();
        if (!brandId) {
            return;
        }
        if (this.standardRequestInFlight) {
            return;
        }
        this.standardRequestInFlight = true;
        try {
            const response = await drugStore.saveStandardRetentionPeriods({ brandId, retentionPeriods: [] });
            if (response && response.status < 400) {
                const { selectedItem } = this.state;
                this.setState({
                    standardSaved: false,
                    selectedItem: { ...selectedItem, retentionPeriods: [] },
                });
            } else {
                (await appAlert('Failed to remove standard. Please try again.'));
            }
        } finally {
            this.standardRequestInFlight = false;
        }
    }

    displayUsageReason = usageReason => {
        switch (usageReason) {
            case '1':
                return '1: Andre';
            case '2':
                return '2: Forebyggende';
            case '3':
                return '3: Sykdom';
            default:
                return '';
        }
    }

    displayThreatmentBy = threatmentBy => {
        switch (threatmentBy) {
            case '1':
                return '1: Dyreeier/medhjelper';
            case '2':
                return '2: Dyrehelsepersonell';
            default:
                return '';
        }
    }

    componentDidMount() {
        this.loadAll();
    }

    render() {
        const { selectedItem, showAddRetentionPeriode, standardSaved, drugProductMap = {} } = this.state;
        const {
            retentionDays,
            drugTitle,
            id: jId,
            object,
        } = this.props;
        const { userStore, drugStore, saleStore, journalStore } = this.props.stores;
        const { user, isAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { newSale, animalJournals: saleAnimalJournals } = saleStore;
        const { journals: fallbackJournals, animalJournals: journalStoreAnimalJournals, newJournal } = journalStore;

        const journalId = parseInt(jId || object?.id, 10);

        // Search in ALL arrays for existing journal
        let journal = null;

        if (fallbackJournals && fallbackJournals.length > 0) {
            journal = fallbackJournals.find(j => j.id === journalId);
        }
        if (!journal && journalStoreAnimalJournals && journalStoreAnimalJournals.length > 0) {
            journal = journalStoreAnimalJournals.find(j => j.id === journalId);
        }
        if (!journal && saleAnimalJournals && saleAnimalJournals.length > 0) {
            journal = saleAnimalJournals.find(j => j.id === journalId);
        }

        // If journal not found in arrays, but we have a valid object prop, use it
        if (!journal && object && object.id === journalId) {
            journal = object;
        }
        // Also handle objects without ID (new journals)
        if (!journal && object && !object.id) {
            journal = object;
        }

        // Default to empty object if still not found
        if (!journal) {
            journal = {};
        }

        const { medicine = [] } = journal;
        const { inputMedicine, inputRetention } = drugStore;

        const currentAnimal = this.getJournalAnimal();
        const drugSearchQuery = currentAnimal?.speciesId ? { speciesId: currentAnimal.speciesId } : {};

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='padding-top: 20px; padding-bottom: 100px;'>
{/* <xmp>{JSON.stringify(inputMedicine, null, 2)}</xmp> */}
{/* <xmp>{JSON.stringify(inputRetention, null, 2)}</xmp> */}
{/* jId: {jId} */}

                    <div class='w-100 d-flex flex-sm-row flex-column'>
                        <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.add-medicine'>Add medicine</Text>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                <DyrejournalSaleSearch
                                    setFocusCallback={fn => this.focusSearch = fn}
                                    stores={this.props.stores}
                                    query={drugSearchQuery}
                                    key={'drugs'}
                                    keyName={'drugs'}
                                    title={drugTitle}
                                    holdingStore={drugStore}
                                    searchResultList={'searchResults'}
                                    filterFields={[
                                        { field: 'legemiddelformKortDn', label: 'Form' },
                                        { field: 'pakningsinfoPakningsstr', label: 'Pakning', formatValue: v => `${v} stk` },
                                    ]}
                                    searchResultsLine={(e, idx, selectedIndex) => {
                                        return(<>
                                            <div class={`d-flex flex-column px-3 py-1 ${idx > 0 ? 'border-top' : ''} ${idx === selectedIndex ? 'bg-primary-light' : ''}`}>
                                                <div class='d-flex flex-row justify-content-between align-items-start'>
                                                    <div>
                                                        <strong>{e.navnFormStyrke || e.navn}</strong>
                                                    </div>
                                                    <div class='d-flex flex-row flex-wrap justify-content-end align-items-center gap-1 ms-2'>
                                                        <DrugProductLinkBadge product={drugProductMap[e.id]} />
                                                        {e.legemiddelformKortDn && <span class='badge bg-body-secondary text-body-secondary' style='font-size: 0.7em; white-space: nowrap;'>
                                                            {e.legemiddelformKortDn}
                                                        </span>}
                                                        {e.pakningsinfoPakningsstr && <span class='badge bg-info-subtle text-info-emphasis' style='font-size: 0.7em; white-space: nowrap;'>
                                                            <i class='fa-duotone fa-box me-1' />{e.pakningsinfoPakningsstr} {e.pakningsinfoEnhetPakningV}
                                                        </span>}
                                                    </div>
                                                </div>
                                                <div class='d-flex flex-row flex-wrap text-body-secondary' style='font-size: 0.85em;'>
                                                    {e.varenr && <span>Varenr: {e.varenr}</span>}
                                                    {e.prisAUP > 0 && <><span class='mx-1'>|</span><span>{e.priceSource === 'VESO' ? 'Innkjøpspris' : 'Pris'}: {e.prisAUP} {e.prisCurrency || 'NOK'}</span></>}
                                                    <FelleskatalogenLink drug={e} class='ms-auto' />
                                                </div>
                                                {(e.amountPerAnimal || e.routeOfAdministration) && <div class='text-info' style='font-size: 0.8em;'>
                                                    {e.amountPerAnimal}
                                                    {e.unit ? ` ${e.unit}` : ''}
                                                    {e.routeOfAdministration ? ` (${e.routeOfAdministration})` : ''}
                                                </div>}
                                            </div>
                                        </>);
                                    }}
                                    callback={this.searchDrugCallback}
                                    focusOnMount={true}
                                />
                                <div class={`table-responsive ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} mt-3`}>
                                    <table class='table table-striped table-sm mb-0'>
                                        <thead>
                                            <tr>
                                                <th>&nbsp;</th>
                                                <th><Text id='dyrejournal.medicine'>Medicine</Text></th>
                                                <th><Text id='dyrejournal.medicine-amount-per-animal'>Amount (each animal)</Text></th>
                                                <th><Text id='dyrejournal.medicine-unit'>Unit</Text></th>
                                                <th><Text id='dyrejournal.medicine-route-of-administration'>Route</Text></th>
                                                <th><Text id='dyrejournal.medicine-no'>No.</Text></th>
                                                <th><Text id='dyrejournal.medicine-last-threatment'>Last threatment</Text></th>
                                                <th><Text id='dyrejournal.medicine-usage-reason'>Usage reason</Text></th>
                                                <th><Text id='dyrejournal.medicine-threatment-by'>Threatment by</Text></th>
                                                <th><Text id='dyrejournal.medicine-retention-periods'>Retention periods</Text></th>
                                                <th><Text id='dyrejournal.medicine-product-number'>Prod num</Text></th>
                                                <th>&nbsp;</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {medicine && medicine.length > 0 && medicine.map((e, idx) => {
                                                return(<>
                                                    <tr data-md5={e.md5}>
                                                        <td onClick={this.onClickEditLine}>{e.md5 === inputMedicine.md5 ? <i class='fa-duotone fa-circle fa-bounce' /> : <i class='fa-duotone fa-pen' />}</td>
                                                        <td>{e.fullname || e.name}</td>
                                                        <td>{e.amountPerAnimal}</td>
                                                        <td>{e.unit}</td>
                                                        <td>{e.routeOfAdministration}</td>
                                                        <td>{e.no}</td>
                                                        <td>{e.lastThreatment ? util.formatDate(e.lastThreatment, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' }) : ''}</td>
                                                        <td>{this.displayUsageReason(e.usageReason)}</td>
                                                        <td>{this.displayThreatmentBy(e.threatmentBy)}</td>
                                                        <td>{this.displayRetentionPeriods(e.retentionPeriods)}</td>
                                                        <td>{e.productNumber}</td>
                                                        <td>
                                                            <button
                                                                class='btn btn-danger btn-sm py-1 rounded-pill'
                                                                onClick={this.onClickDeleteDrugFromJournal}
                                                                data-md5={e.md5}
                                                            >
                                                                <nobr>
                                                                    <i class='fa-solid fa-trash me-1' />
                                                                    <Text id='input.delete'>Delete</Text>
                                                                </nobr>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                </>);
                                            })}

                                            {selectedItem && selectedItem.id && <>
                                                {inputMedicine.md5 ? <>
                                                    <tr class='bg-secondary'>
                                                        <th colspan='12'><Text id='dyrejournal.medicine-edit'>Editing medicine</Text></th>
                                                    </tr>
                                                    <tr class='bg-secondary'>
                                                        <th>&nbsp;</th>
                                                        <td><small>{inputMedicine.name}</small></td>
                                                        <td><input type='text' class='form-control form-control-sm' name='amountPerAnimal' value={inputMedicine.amountPerAnimal} data-md5={selectedItem.md5} onInput={this.onInput} /></td>
                                                        <td><input type='text' class='form-control form-control-sm' name='unit' value={inputMedicine.unit} data-md5={selectedItem.md5} onInput={this.onInput} /></td>
                                                        <td><input type='text' class='form-control form-control-sm' name='routeOfAdministration' value={inputMedicine.routeOfAdministration} data-md5={selectedItem.md5} onInput={this.onInput} placeholder='e.g. IV, IM, SC, PO' /></td>
                                                        <td><input type='text' class='form-control form-control-sm' name='no' value={inputMedicine.no} data-md5={selectedItem.md5} onInput={this.onInput} /></td>
                                                        <td><input type='datetime-local' class='form-control form-control-sm' name='lastThreatment' value={inputMedicine.lastThreatment} data-md5={selectedItem.md5} onInput={this.onInput} /></td>
                                                        <td>
                                                            <select class='form-control form-control-sm' name='usageReason' value={inputMedicine.usageReason} data-md5={selectedItem.md5} onInput={this.onInput}>
                                                                <option value='1'>1: Andre</option>
                                                                <option value='2'>2: Forebyggende</option>
                                                                <option value='3'>3: Sykdom</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <select class='form-control form-control-sm' name='threatmentBy' value={inputMedicine.threatmentBy} data-md5={selectedItem.md5} onInput={this.onInput}>
                                                                <option value='1'>1: Dyreeier/medhjelper</option>
                                                                <option value='2'>2: Dyrehelsepersonell</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <small>{this.displayRetentionPeriods(inputMedicine.retentionPeriods, true)}</small>
                                                            {showAddRetentionPeriode ? <>
                                                                <div class='d-flex flex-column'>
                                                                    <select class='form-control form-control-sm' name='retentionType' value={inputRetention.type} onInput={this.onInputRetention}>
                                                                        <option value=''>-- <Text id='input.select-retention-type'>Select retention type</Text></option>
                                                                        <option value='Produkt'>Produkt</option>
                                                                        <option value='Slakt'>Slakt</option>
                                                                        <option value='Startnekt'>Startnekt</option>
                                                                    </select>
                                                                    <select class='form-control form-control-sm' name='retentionDyreslag' value={inputRetention.dyreslag} onInput={this.onInputRetention}>
                                                                        <option value=''><Text id='input.select-animal-kind'>Select dyreslag</Text></option>
                                                                        <option value='Storfe'>Storfe</option>
                                                                        <option value='Geit'>Geit</option>
                                                                        <option value='Gris'>Gris</option>
                                                                        <option value='Hest'>Hest</option>
                                                                        <option value='Sau'>Sau</option>
                                                                        <option value='Fjørfe'>Fjørfe</option>
                                                                    </select>
                                                                    <input type='number' min='0' max='999' class='form-control form-control-sm' name='retentionDays' value={inputRetention.days} onInput={this.onInputRetention} placeholder={retentionDays} disabled={inputRetention.retentionLivstid} />
                                                                    <div class='form-check mt-1'>
                                                                        <input class='form-check-input' type='checkbox' name='retentionLivstid' checked={inputRetention.retentionLivstid} onChange={this.onToggleRetentionLivstid} />
                                                                        <label class='form-check-label'>
                                                                            <Text id='dyrejournal.retention-lifetime'>Lifetime (livstid)</Text>
                                                                        </label>
                                                                    </div>

                                                                    <button class='btn btn-primary rounded-pill btn-sm py-1 mt-1' onClick={this.addRetentionPeriode}>
                                                                        <i class='fa-duotone fa-plus' /> <Text id='input.add'>Add</Text>
                                                                    </button>
                                                                    <button class='btn btn-outline-secondary btn-sm rounded-pill py-1 mt-1' onClick={this.toggleAddRetentionPeriode}>
                                                                        <Text id='input.cancel'>Cancel</Text>
                                                                    </button>
                                                                </div>
                                                            </> : <>
                                                                <div class='d-flex flex-row justify-content-between'>
                                                                    <button class='btn btn-outline-primary btn-sm rounded-pill py-1' onClick={this.toggleAddRetentionPeriode}>
                                                                        <i class='fa-duotone fa-plus' />
                                                                        <Text id='input.add-retention-period'>Add retention period</Text>
                                                                    </button>
                                                                </div>
                                                            </>}
                                                            {!!inputMedicine.retentionPeriods?.length && !!this.getStandardBrandId() && <div class='d-flex flex-row align-items-center mt-1'>
                                                                {standardSaved ? <span class='text-success'>
                                                                    <i class='fa-solid fa-check me-1' />
                                                                    <Text id='dyrejournal.retention-standard-saved'>Standard saved</Text>
                                                                </span> : <button class='btn btn-outline-success btn-sm rounded-pill py-1' onClick={this.onClickSaveStandardRetention}>
                                                                    <i class='fa-duotone fa-star me-1' />
                                                                    <Text id='dyrejournal.retention-save-standard'>Save as standard</Text>
                                                                </button>}
                                                            </div>}
                                                        </td>
                                                        <td><input type='text' class='form-control form-control-sm' name='productNumber' value={inputMedicine.productNumber} data-md5={selectedItem.md5} onInput={this.onInput} /></td>
                                                        <td>
                                                            <button class='btn btn-primary btn-sm py-1 rounded-pill' onClick={this.onClickEditDone}>
                                                                <nobr>
                                                                    <i class='fa-solid fa-floppy-disk me-1' />
                                                                    <Text id='input.done'>Done</Text>
                                                                </nobr>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                </> : <>
                                                    <tr>
                                                        <th>&nbsp;</th>
                                                        <td><small>{selectedItem.navnFormStyrke || selectedItem.navn}</small></td>
                                                        <td><input ref={c => this.amountPerAnimalRef = c} type='text' class='form-control form-control-sm' name='amountPerAnimal' value={inputMedicine.amountPerAnimal} onInput={this.onInput} /></td>
                                                        <td><input type='text' class='form-control form-control-sm' name='unit' value={inputMedicine.unit} onInput={this.onInput} /></td>
                                                        <td><input type='text' class='form-control form-control-sm' name='routeOfAdministration' value={inputMedicine.routeOfAdministration} onInput={this.onInput} placeholder='e.g. IV, IM, SC, PO' /></td>
                                                        <td><input type='text' class='form-control form-control-sm' name='no' value={inputMedicine.no || ''} onInput={this.onInput} /></td>
                                                        <td><input type='datetime-local' class='form-control form-control-sm' name='lastThreatment' value={inputMedicine.lastThreatment} onInput={this.onInput} /></td>
                                                        <td>
                                                            <select class='form-control form-control-sm' name='usageReason' value={inputMedicine.usageReason} data-md5={selectedItem.md5} onInput={this.onInput}>
                                                                <option value='1'>1: Andre</option>
                                                                <option value='2'>2: Forebyggende</option>
                                                                <option value='3'>3: Sykdom</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <select class='form-control form-control-sm' name='threatmentBy' value={inputMedicine.threatmentBy} data-md5={selectedItem.md5}  onInput={this.onInput}>
                                                                <option value='1'>1: Dyreeier/medhjelper</option>
                                                                <option value='2'>2: Dyrehelsepersonell</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <small>
                                                                {this.displayRetentionPeriods(inputMedicine.retentionPeriods, true)}
                                                            </small>
                                                            {selectedItem.retentionPeriods?.length > 0 && <div class='d-flex flex-row align-items-center mb-1'>
                                                                <span class='badge text-bg-info'>
                                                                    <Text id='dyrejournal.retention-standard-badge'>Clinic standard</Text>
                                                                </span>
                                                                <button class='btn btn-link btn-sm p-0 ms-2 text-danger' onClick={this.onClickRemoveStandardRetention}>
                                                                    <Text id='dyrejournal.retention-standard-remove'>Remove standard</Text>
                                                                </button>
                                                            </div>}
                                                            {showAddRetentionPeriode ? <>
                                                                <div class='d-flex flex-column'>
                                                                    <select class='form-control form-control-sm' name='retentionType' value={inputRetention.type} onInput={this.onInputRetention}>
                                                                        <option value=''>-- <Text id='input.select-retention-type'>Select retention type</Text></option>
                                                                        <option value='Produkt'>Produkt</option>
                                                                        <option value='Slakt'>Slakt</option>
                                                                        <option value='Startnekt'>Startnekt</option>
                                                                    </select>
                                                                    <select class='form-control form-control-sm' name='retentionDyreslag' value={inputRetention.dyreslag} onInput={this.onInputRetention}>
                                                                        <option value=''><Text id='input.select-animal-kind'>Select dyreslag</Text></option>
                                                                        <option value='Storfe'>Storfe</option>
                                                                        <option value='Geit'>Geit</option>
                                                                        <option value='Gris'>Gris</option>
                                                                        <option value='Hest'>Hest</option>
                                                                        <option value='Sau'>Sau</option>
                                                                        <option value='Fjørfe'>Fjørfe</option>
                                                                    </select>
                                                                    <input type='number' min='0' max='999' class='form-control form-control-sm' name='retentionDays' value={inputRetention.days} onInput={this.onInputRetention} placeholder={retentionDays} disabled={inputRetention.retentionLivstid} />
                                                                    <div class='form-check mt-1'>
                                                                        <input class='form-check-input' type='checkbox' name='retentionLivstid' checked={inputRetention.retentionLivstid} onChange={this.onToggleRetentionLivstid} />
                                                                        <label class='form-check-label'>
                                                                            <Text id='dyrejournal.retention-lifetime'>Lifetime (livstid)</Text>
                                                                        </label>
                                                                    </div>

                                                                    <button class='btn btn-primary rounded-pill btn-sm py-1 mt-1' onClick={this.addRetentionPeriode}>
                                                                        <i class='fa-duotone fa-plus' /> <Text id='input.add'>Add</Text>
                                                                    </button>
                                                                    <button class='btn btn-outline-secondary btn-sm rounded-pill py-1 mt-1' onClick={this.toggleAddRetentionPeriode}>
                                                                        <Text id='input.cancel'>Cancel</Text>
                                                                    </button>
                                                                </div>
                                                            </> : <>
                                                                <div class='d-flex flex-row justify-content-between'>

                                                                    <button class='btn btn-outline-primary btn-sm rounded-pill py-1' onClick={this.toggleAddRetentionPeriode}>
                                                                        <i class='fa-duotone fa-plus' />
                                                                        <Text id='input.add-retention-period'>Add retention period</Text>
                                                                    </button>

                                                                    <button
                                                                        class='btn btn-outline-danger btn-sm rounded-pill py-1'
                                                                        onClick={this.onClickRemoveRetentionPeriode}
                                                                        data-md5={selectedItem.md5}
                                                                    >
                                                                        <i class='fa-duotone fa-trash' />
                                                                        <Text id='input.delete-retention'>Delete all retentions</Text>
                                                                    </button>

                                                                </div>
                                                            </>}
                                                            {!!inputMedicine.retentionPeriods?.length && !!this.getStandardBrandId() && <div class='d-flex flex-row align-items-center mt-1'>
                                                                {standardSaved ? <span class='text-success'>
                                                                    <i class='fa-solid fa-check me-1' />
                                                                    <Text id='dyrejournal.retention-standard-saved'>Standard saved</Text>
                                                                </span> : <button class='btn btn-outline-success btn-sm rounded-pill py-1' onClick={this.onClickSaveStandardRetention}>
                                                                    <i class='fa-duotone fa-star me-1' />
                                                                    <Text id='dyrejournal.retention-save-standard'>Save as standard</Text>
                                                                </button>}
                                                            </div>}
                                                        </td>
                                                        <td><input type='text' class='form-control form-control-sm' name='productNumber' value={inputMedicine.productNumber} onInput={this.onInput} /></td>
                                                        <td>
                                                            <nobr>
                                                                <button class='btn btn-primary btn-sm py-1 rounded-pill' onClick={this.onClickAddDrugToJournal}>
                                                                    <i class='fa-solid fa-plus me-1' />
                                                                    <Text id='input.add'>Add</Text>
                                                                </button>
                                                            </nobr>
                                                        </td>
                                                    </tr>
                                                </>}
                                            </>}
                                        </tbody>
                                    </table>
                                </div>
                                {/* <xmp>{JSON.stringify(selectedItem, null, 2)}</xmp> */}

                            </div>
                        </div>
                        {/* <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.report.journals'>Journals</Text>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                Report for today's journals
                            </div>
                        </div> */}
                    </div>

                </div>
            </div>
        </>);
    }
}

export default AddMedicine;
